import {
  UIAutomationTrigger,
  UIWorkflowConditionType,
  UIItemWorkflowTriggerCondition,
  TriggerDueDateArrivedInfo,
  TriggerTimeFrequencyInfo,
  UIWorkflowAction,
  UIWorkflowCondition,
  WorkflowContextItem,
  WorkflowCycleOptions,
  WorkflowFilterOptions,
  WorkflowTimeFrequency,
  UIWorkflowTriggerGroup,
  WorkflowUIConditionProperties,
  UIWorkflowActionGroup,
  DueDateArrivedInputParams,
} from '@/models/workflow.models';
import { computed, ref } from 'vue';
import { useCustomFields } from '@/utils/custom-fields';
import camelCase from 'lodash-es/camelCase';
import startCase from 'lodash-es/startCase';
import { InputVariable, WEEKDAYS } from '@/models/common.models';
import { useWorkflowStore } from '@/store/workflowStore';
import { hourWithMeridiem, utcHourToLocalHour } from './time';
import {
  CustomField,
  Folder,
  WorkItem,
  CreateWorkflow,
  UpdateFolderActionData,
  UpdateWorkItemActionData,
  Workflow,
  WorkflowAction,
  WorkflowAllConditions,
  WorkflowAnyConditions,
  WorkflowConditionProperties,
  WorkflowNestedCondition,
  WorkflowOperator,
  WorkflowTrigger,
} from '@/api';

export function useAutomations() {
  const numberConditionTypes: Array<UIWorkflowCondition> = [
    {
      type: UIWorkflowConditionType.FIELD_VALUE,
      operator: WorkflowOperator.EQUAL,
      label: 'is equal to',
    },
    {
      type: UIWorkflowConditionType.FIELD_VALUE,
      operator: WorkflowOperator.NOT_EQUAL,
      label: 'is not equal to',
    },
    {
      type: UIWorkflowConditionType.FIELD_VALUE,
      operator: WorkflowOperator.GREATER_THAN,
      label: 'is greater than',
    },
    {
      type: UIWorkflowConditionType.FIELD_VALUE,
      operator: WorkflowOperator.GREATER_THAN_INCLUSIVE,
      label: 'is greater than or equal to',
    },
    {
      type: UIWorkflowConditionType.FIELD_VALUE,
      operator: WorkflowOperator.LESS_THAN,
      label: 'is less than',
    },
    {
      type: UIWorkflowConditionType.FIELD_VALUE,
      operator: WorkflowOperator.LESS_THAN_INCLUSIVE,
      label: 'is less than or equal to',
    },
  ];

  const sharedItemConditions: Array<UIItemWorkflowTriggerCondition> = [
    {
      field: 'parentNumber',
      label: 'Direct Parent',
      icon: 'workstream-empty',
      iconComponent: 'custom-icon',
      types: [
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.EQUAL,
          label: 'is equal to',
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.NOT_EQUAL,
          label: 'is not equal to',
        },
      ],
    },
    {
      field: 'hierarchy',
      label: 'Parent Hierarchy',
      icon: 'workstream-core',
      iconComponent: 'custom-icon',
      types: [
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.CONTAINS,
          label: 'contains',
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.DOES_NOT_CONTAIN,
          label: 'does not contain',
        },
      ],
    },
    {
      field: 'color',
      label: 'Color',
      icon: 'eyedropper',
      iconComponent: 'fluent-icon',
      types: [
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.EQUAL,
          label: 'is equal to',
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.NOT_EQUAL,
          label: 'is not equal to',
        },
      ],
    },
  ];

  const workItemConditions: Array<UIItemWorkflowTriggerCondition> = [
    ...sharedItemConditions,
    {
      field: 'workTypeId',
      label: 'Item Type',
      icon: 'apps',
      iconComponent: 'fluent-icon',
      types: [
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.EQUAL,
          label: 'is equal to',
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.NOT_EQUAL,
          label: 'is not equal to',
        },
      ],
    },
    {
      field: 'priorityId',
      label: 'Priority',
      icon: 'warning',
      iconComponent: 'fluent-icon',
      types: [
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.EQUAL,
          label: 'is equal to',
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.NOT_EQUAL,
          label: 'is not equal to',
        },
      ],
    },
    {
      field: 'authorUsername',
      label: 'Author',
      icon: 'person-add',
      iconComponent: 'octicon',
      types: [
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.EQUAL,
          label: 'is equal to',
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.NOT_EQUAL,
          label: 'is not equal to',
        },
      ],
    },
    {
      field: 'labels',
      label: 'Tags',
      icon: 'tag',
      iconComponent: 'octicon',
      types: [
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.CONTAINS,
          label: 'contain',
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.DOES_NOT_CONTAIN,
          label: 'does not contain',
        },
      ],
    },
    {
      field: 'estimate',
      label: 'Time Estimate',
      icon: 'clock',
      iconComponent: 'custom-icon',
      types: [
        ...numberConditionTypes,
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.IS_SET,
          label: 'is set',
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.NOT_SET,
          label: 'is not set',
        },
      ],
    },
    {
      field: 'pointEffort',
      label: 'Effort Points',
      icon: 'barbell',
      iconComponent: 'custom-icon',
      types: [
        ...numberConditionTypes,
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.IS_SET,
          label: 'is set',
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.NOT_SET,
          label: 'is not set',
        },
      ],
    },
    {
      field: 'laneId',
      label: 'Status',
      icon: 'column-outline',
      iconComponent: 'custom-icon',
      types: [
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.EQUAL,
          label: 'is equal to',
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.NOT_EQUAL,
          label: 'is not equal to',
        },
      ],
    },
    {
      field: 'teamId',
      label: 'Team',
      icon: 'people',
      iconComponent: 'custom-icon',
      types: [
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.EQUAL,
          label: 'is equal to',
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.NOT_EQUAL,
          label: 'is not equal to',
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.IS_SET,
          label: 'is set',
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.NOT_SET,
          label: 'is not set',
        },
      ],
    },
    {
      field: 'boardId',
      label: 'Board',
      icon: 'column-outline',
      iconComponent: 'custom-icon',
      types: [
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.EQUAL,
          label: 'is equal to',
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.NOT_EQUAL,
          label: 'is not equal to',
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.IS_SET,
          label: 'is set',
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.NOT_SET,
          label: 'is not set',
        },
      ],
    },
    {
      field: 'boardType',
      label: 'Cycle',
      icon: 'calendar_sync',
      iconComponent: 'fluent-icon',
      types: [
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.EQUAL,
          label: 'is a current cycle',
          value: WorkflowCycleOptions.CURRENT,
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.EQUAL,
          label: 'is a future cycle',
          value: WorkflowCycleOptions.FUTURE,
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.EQUAL,
          label: 'is a past cycle',
          value: WorkflowCycleOptions.PAST,
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.EQUAL,
          label: 'is not set',
          value: null,
        },
      ],
    },
    {
      field: 'assigneeUsername',
      label: 'Assignee',
      icon: 'person',
      iconComponent: 'octicon',
      types: [
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.EQUAL,
          label: 'is equal to',
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.NOT_EQUAL,
          label: 'is not equal to',
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.IS_SET,
          label: 'is set',
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.NOT_SET,
          label: 'is not set',
        },
      ],
    },
    {
      field: 'customFields',
      label: 'Custom Field',
      icon: 'custom-fields',
      iconComponent: 'custom-icon',
      types: [
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.EQUAL,
          label: 'is equal to',
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.NOT_EQUAL,
          label: 'is not equal to',
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.IS_SET,
          label: 'is set',
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.NOT_SET,
          label: 'is not set',
        },
      ],
    },
    {
      field: 'dueDate',
      label: 'Due Date',
      icon: 'calendar',
      iconComponent: 'octicon',
      types: [
        {
          type: UIWorkflowConditionType.DATE,
          operator: WorkflowOperator.EQUAL,
          label: 'is equal to',
        },
        {
          type: UIWorkflowConditionType.DATE,
          operator: WorkflowOperator.NOT_EQUAL,
          label: 'is not equal to',
        },
        {
          type: UIWorkflowConditionType.DATE,
          operator: WorkflowOperator.GREATER_THAN,
          label: 'is greater than',
        },
        {
          type: UIWorkflowConditionType.DATE,
          operator: WorkflowOperator.GREATER_THAN_INCLUSIVE,
          label: 'is greater than or equal to',
        },
        {
          type: UIWorkflowConditionType.DATE,
          operator: WorkflowOperator.LESS_THAN,
          label: 'is less than',
        },
        {
          type: UIWorkflowConditionType.DATE,
          operator: WorkflowOperator.LESS_THAN_INCLUSIVE,
          label: 'is less than or equal to',
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.IS_SET,
          label: 'is set',
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.NOT_SET,
          label: 'is not set',
        },
      ],
    },
    {
      field: 'timeSpentSeconds',
      label: 'Time Spent',
      icon: 'stopwatch',
      iconComponent: 'octicon',
      types: [
        ...numberConditionTypes,
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.IS_SET,
          label: 'is set',
        },
        {
          type: UIWorkflowConditionType.FIELD_VALUE,
          operator: WorkflowOperator.NOT_SET,
          label: 'is not set',
        },
      ],
    },
  ];

  const workstreamConditions: Array<UIItemWorkflowTriggerCondition> = [
    ...sharedItemConditions,
  ];

  const workItemContext: Array<WorkflowContextItem> = [
    {
      field: '$workitem',
      type: 'workitem',
      label: 'Workitem',
    },
  ];

  const workstreamContext: Array<WorkflowContextItem> = [
    {
      field: '$folder',
      type: 'folder',
      label: 'Workstream',
    },
  ];

  const messageContext: Array<WorkflowContextItem> = [
    {
      field: '$recipient',
      type: 'organizationMembership',
      label: 'Recipient',
    },
  ];

  const itemRequestContext: Array<WorkflowContextItem> = [
    {
      field: '$itemRequest',
      type: 'itemRequest',
      label: 'Item Request',
    },
    {
      field: '$workItem',
      type: 'workitem',
      label: 'Workitem',
    },
  ];

  const itemMinMaxConditions = {
    minConditions: 0,
    maxConditions: 10,
  };

  const workItemUpdatedSharedTriggerAttrs = {
    workflowTrigger: WorkflowTrigger.ITEM_UPDATED,
    possibleConditions: workItemConditions,
    context: workItemContext,
    ...itemMinMaxConditions,
  };

  const workstreamUpdatedSharedTriggerAttrs = {
    workflowTrigger: WorkflowTrigger.FOLDER_UPDATED,
    possibleConditions: workstreamConditions,
    context: workstreamContext,
    ...itemMinMaxConditions,
  };

  const triggerGroups: Array<UIWorkflowTriggerGroup> = [
    {
      label: 'Time Based',
      icon: 'clock',
      iconComponent: 'custom-icon',
      triggers: [
        {
          type: WorkflowTrigger.SCHEDULE,
          label: 'Every Time Period',
          icon: 'clock',
          iconComponent: 'custom-icon',
          workflowTrigger: WorkflowTrigger.SCHEDULE,
          possibleConditions: [],
          context: [],
          minConditions: 1,
          maxConditions: 1,
        },
        {
          type: WorkflowTrigger.DUE_DATE_ARRIVED,
          label: 'Due Date Arrived',
          icon: 'calendar',
          iconComponent: 'octicon',
          workflowTrigger: WorkflowTrigger.DUE_DATE_ARRIVED,
          possibleConditions: workItemConditions,
          context: workItemContext,
          ...itemMinMaxConditions,
        },
      ],
    },
    {
      label: 'Work Item',
      icon: 'note',
      iconComponent: 'octicon',
      triggers: [
        {
          type: WorkflowTrigger.ITEM_CREATED,
          label: 'Work Item Created',
          icon: 'plus-circle',
          iconComponent: 'octicon',
          workflowTrigger: WorkflowTrigger.ITEM_CREATED,
          possibleConditions: workItemConditions,
          context: workItemContext,
          ...itemMinMaxConditions,
        },
        {
          type: WorkflowTrigger.ITEM_ARCHIVED,
          label: 'Work Item Deleted',
          icon: 'trash',
          iconComponent: 'octicon',
          workflowTrigger: WorkflowTrigger.ITEM_ARCHIVED,
          possibleConditions: workItemConditions,
          context: workItemContext,
          ...itemMinMaxConditions,
        },
        {
          type: WorkflowTrigger.ITEM_UPDATED,
          field: 'workTypeId',
          label: 'Item Type Changed',
          icon: 'apps',
          iconComponent: 'fluent-icon',
          ...workItemUpdatedSharedTriggerAttrs,
        },
        {
          type: WorkflowTrigger.ITEM_UPDATED,
          field: 'parentNumber',
          label: 'Item Parent Changed',
          icon: 'workstream-arrow',
          iconComponent: 'custom-icon',
          ...workItemUpdatedSharedTriggerAttrs,
        },
        {
          type: WorkflowTrigger.ITEM_UPDATED,
          field: 'color',
          label: 'Item Color Changed',
          icon: 'eyedropper',
          iconComponent: 'fluent-icon',
          ...workItemUpdatedSharedTriggerAttrs,
        },
        {
          type: WorkflowTrigger.ITEM_UPDATED,
          field: 'laneId',
          label: 'Status Changed',
          icon: 'column-outline',
          iconComponent: 'custom-icon',
          ...workItemUpdatedSharedTriggerAttrs,
        },
        {
          type: WorkflowTrigger.ITEM_UPDATED,
          field: 'priorityId',
          label: 'Priority Changed',
          icon: 'warning',
          iconComponent: 'fluent-icon',
          ...workItemUpdatedSharedTriggerAttrs,
        },
        {
          type: WorkflowTrigger.ITEM_UPDATED,
          field: 'teamId',
          label: 'Team Updated',
          icon: 'people',
          iconComponent: 'custom-icon',
          ...workItemUpdatedSharedTriggerAttrs,
        },
        {
          type: WorkflowTrigger.ITEM_UPDATED,
          field: 'assigneeUsername',
          label: 'Assignee Updated',
          icon: 'person',
          iconComponent: 'octicon',
          ...workItemUpdatedSharedTriggerAttrs,
        },
        {
          type: WorkflowTrigger.ITEM_UPDATED,
          field: 'pointEffort',
          label: 'Point Effort Updated',
          icon: 'barbell',
          iconComponent: 'custom-icon',
          ...workItemUpdatedSharedTriggerAttrs,
        },
        {
          type: WorkflowTrigger.ITEM_UPDATED,
          field: 'estimate',
          label: 'Time Estimate Updated',
          icon: 'clock',
          iconComponent: 'custom-icon',
          ...workItemUpdatedSharedTriggerAttrs,
        },
        {
          type: WorkflowTrigger.ITEM_UPDATED,
          field: 'boardId',
          label: 'Cycle/Board Updated',
          icon: 'calendar_sync',
          iconComponent: 'fluent-icon',
          ...workItemUpdatedSharedTriggerAttrs,
        },
        {
          type: WorkflowTrigger.ITEM_UPDATED,
          field: 'customFields',
          label: 'Custom Field Updated',
          icon: 'custom-fields',
          iconComponent: 'custom-icon',
          ...workItemUpdatedSharedTriggerAttrs,
        },
        {
          type: WorkflowTrigger.ITEM_UPDATED,
          field: 'labels',
          label: 'Tags Updated',
          icon: 'tag',
          iconComponent: 'octicon',
          ...workItemUpdatedSharedTriggerAttrs,
        },
        {
          type: WorkflowTrigger.ITEM_UPDATED,
          field: 'dueDate',
          label: 'Due Date Changed',
          icon: 'calendar',
          iconComponent: 'octicon',
          ...workItemUpdatedSharedTriggerAttrs,
        },
        {
          type: WorkflowTrigger.ITEM_UPDATED,
          field: 'timeSpentSeconds',
          label: 'Time Spent Updated',
          icon: 'stopwatch',
          iconComponent: 'octicon',
          ...workItemUpdatedSharedTriggerAttrs,
        },
      ],
    },
    {
      label: 'Workstream',
      icon: 'workstream-100',
      iconComponent: 'custom-icon',
      triggers: [
        {
          type: WorkflowTrigger.FOLDER_CREATED,
          label: 'Workstream Created',
          icon: 'plus-circle',
          iconComponent: 'octicon',
          workflowTrigger: WorkflowTrigger.FOLDER_CREATED,
          possibleConditions: workstreamConditions,
          context: workstreamContext,
          ...itemMinMaxConditions,
        },
        {
          type: WorkflowTrigger.FOLDER_ARCHIVED,
          label: 'Workstream Deleted',
          icon: 'trash',
          iconComponent: 'octicon',
          workflowTrigger: WorkflowTrigger.FOLDER_ARCHIVED,
          possibleConditions: workstreamConditions,
          context: workstreamContext,
          ...itemMinMaxConditions,
        },
        {
          type: WorkflowTrigger.FOLDER_UPDATED,
          field: 'parentNumber',
          label: 'Workstream Parent Changed',
          icon: 'workstream-arrow',
          iconComponent: 'custom-icon',
          ...workstreamUpdatedSharedTriggerAttrs,
        },
        {
          type: WorkflowTrigger.FOLDER_UPDATED,
          field: 'color',
          label: 'Workstream Color Changed',
          icon: 'eyedropper',
          iconComponent: 'fluent-icon',
          ...workstreamUpdatedSharedTriggerAttrs,
        },
      ],
    },
    {
      label: 'Item Request',
      icon: 'document_arrow_right',
      iconComponent: 'fluent-icon',
      triggers: [
        {
          type: WorkflowTrigger.ITEM_REQUEST_SUBMITTED,
          label: 'Item Request Submitted',
          icon: 'document_add',
          iconComponent: 'fluent-icon',
          workflowTrigger: WorkflowTrigger.ITEM_REQUEST_SUBMITTED,
          possibleConditions: [],
          context: itemRequestContext,
          minConditions: 1,
          maxConditions: 1,
        },
      ],
    },
    {
      label: 'Github',
      icon: 'mark-github',
      iconComponent: 'octicon',
      triggers: [
        {
          type: WorkflowTrigger.ITEM_UPDATED,
          label: 'Pull Request Opened',
          operator: WorkflowOperator.PULL_REQUEST_OPENED,
          field: 'integrations.github',
          icon: 'git-pull-request',
          iconComponent: 'octicon',
          workflowTrigger: WorkflowTrigger.ITEM_UPDATED,
          possibleConditions: workItemConditions,
          context: workItemContext,
          ...itemMinMaxConditions,
        },
        {
          type: WorkflowTrigger.ITEM_UPDATED,
          label: 'Branch Created',
          operator: WorkflowOperator.BRANCH_CREATED,
          field: 'integrations.github',
          icon: 'git-branch',
          iconComponent: 'octicon',
          workflowTrigger: WorkflowTrigger.ITEM_UPDATED,
          possibleConditions: workItemConditions,
          context: workItemContext,
          ...itemMinMaxConditions,
        },
        {
          type: WorkflowTrigger.ITEM_UPDATED,
          label: 'Commit Created',
          operator: WorkflowOperator.COMMIT_CREATED,
          field: 'integrations.github',
          icon: 'git-commit',
          iconComponent: 'octicon',
          workflowTrigger: WorkflowTrigger.ITEM_UPDATED,
          possibleConditions: workItemConditions,
          context: workItemContext,
          ...itemMinMaxConditions,
        },
        {
          type: WorkflowTrigger.ITEM_UPDATED,
          label: 'All Checks Passed',
          operator: WorkflowOperator.ALL_CHECKS_PASSED,
          field: 'integrations.github',
          icon: 'check-circle',
          iconComponent: 'octicon',
          workflowTrigger: WorkflowTrigger.ITEM_UPDATED,
          possibleConditions: workItemConditions,
          context: workItemContext,
          ...itemMinMaxConditions,
        },
        {
          type: WorkflowTrigger.ITEM_UPDATED,
          label: 'Check Failed',
          operator: WorkflowOperator.CHECK_FAILED,
          field: 'integrations.github',
          icon: 'x-circle',
          iconComponent: 'octicon',
          workflowTrigger: WorkflowTrigger.ITEM_UPDATED,
          possibleConditions: workItemConditions,
          context: workItemContext,
          ...itemMinMaxConditions,
        },
        {
          type: WorkflowTrigger.ITEM_UPDATED,
          label: 'Review Requested',
          operator: WorkflowOperator.REVIEW_REQUESTED,
          field: 'integrations.github',
          icon: 'person',
          iconComponent: 'octicon',
          workflowTrigger: WorkflowTrigger.ITEM_UPDATED,
          possibleConditions: workItemConditions,
          context: workItemContext,
          ...itemMinMaxConditions,
        },
        {
          type: WorkflowTrigger.ITEM_UPDATED,
          label: 'Changes Requested',
          operator: WorkflowOperator.CHANGES_REQUESTED,
          field: 'integrations.github',
          icon: 'file-diff',
          iconComponent: 'octicon',
          workflowTrigger: WorkflowTrigger.ITEM_UPDATED,
          possibleConditions: workItemConditions,
          context: workItemContext,
          ...itemMinMaxConditions,
        },
        {
          type: WorkflowTrigger.ITEM_UPDATED,
          label: 'All Reviews Approved',
          operator: WorkflowOperator.ALL_REVIEWS_APPROVED,
          field: 'integrations.github',
          icon: 'code-review',
          iconComponent: 'octicon',
          workflowTrigger: WorkflowTrigger.ITEM_UPDATED,
          possibleConditions: workItemConditions,
          context: workItemContext,
          ...itemMinMaxConditions,
        },
        {
          type: WorkflowTrigger.ITEM_UPDATED,
          label: 'All Pull Requests Merged/Closed',
          operator: WorkflowOperator.ALL_PULL_REQUESTS_MERGED_CLOSED,
          field: 'integrations.github',
          icon: 'git-merge',
          iconComponent: 'octicon',
          workflowTrigger: WorkflowTrigger.ITEM_UPDATED,
          possibleConditions: workItemConditions,
          context: workItemContext,
          ...itemMinMaxConditions,
        },
      ],
    },
  ];

  const actionGroups: Array<UIWorkflowActionGroup> = [
    {
      label: 'Messaging',
      icon: 'chat',
      iconComponent: 'fluent-icon',
      actions: [
        {
          type: 'send-notification',
          label: 'Send Notification',
          icon: 'bell',
          iconComponent: 'octicon',
          context: messageContext,
        },
        // {
        //   type: 'send-email',
        //   label: 'Send Email',
        //   icon: 'mail',
        //   iconComponent: 'fluent-icon',
        //   context: messageContext,
        // },
      ],
    },
    {
      label: 'Integrations',
      icon: 'plug',
      iconComponent: 'octicon',
      actions: [
        {
          type: 'send-api-request',
          label: 'Call Webhook',
          icon: 'webhook',
          iconComponent: 'octicon',
        },
      ],
    },
    {
      label: 'Work Item',
      icon: 'note',
      iconComponent: 'octicon',
      actions: [
        {
          type: 'create-workitem',
          label: 'Create Work Item',
          icon: 'plus-circle',
          iconComponent: 'octicon',
        },
        {
          type: 'create-subitem',
          label: 'Create Subtask',
          icon: 'task_list',
          iconComponent: 'fluent-icon',
        },
        {
          type: 'update-workitem',
          label: 'Change Item Parent',
          icon: 'workstream-arrow',
          iconComponent: 'custom-icon',
          allowedWorkflowTriggers: [
            WorkflowTrigger.ITEM_CREATED,
            WorkflowTrigger.ITEM_UPDATED,
            WorkflowTrigger.DUE_DATE_ARRIVED,
          ],
        },
        {
          type: 'update-workitem',
          label: 'Change Item Color',
          icon: 'eyedropper',
          iconComponent: 'fluent-icon',
          allowedWorkflowTriggers: [
            WorkflowTrigger.ITEM_CREATED,
            WorkflowTrigger.ITEM_UPDATED,
            WorkflowTrigger.DUE_DATE_ARRIVED,
          ],
        },
        {
          type: 'update-workitem',
          label: 'Change Status',
          icon: 'column-outline',
          iconComponent: 'custom-icon',
          allowedWorkflowTriggers: [
            WorkflowTrigger.ITEM_CREATED,
            WorkflowTrigger.ITEM_UPDATED,
            WorkflowTrigger.DUE_DATE_ARRIVED,
          ],
        },
        {
          type: 'update-workitem',
          label: 'Change Item Type',
          icon: 'apps',
          iconComponent: 'fluent-icon',
          allowedWorkflowTriggers: [
            WorkflowTrigger.ITEM_CREATED,
            WorkflowTrigger.ITEM_UPDATED,
            WorkflowTrigger.DUE_DATE_ARRIVED,
          ],
        },
        {
          type: 'update-workitem',
          label: 'Change Priority',
          icon: 'warning',
          iconComponent: 'fluent-icon',
          allowedWorkflowTriggers: [
            WorkflowTrigger.ITEM_CREATED,
            WorkflowTrigger.ITEM_UPDATED,
            WorkflowTrigger.DUE_DATE_ARRIVED,
          ],
        },
        {
          type: 'update-workitem',
          label: 'Change Team',
          icon: 'people',
          iconComponent: 'custom-icon',
          allowedWorkflowTriggers: [
            WorkflowTrigger.ITEM_CREATED,
            WorkflowTrigger.ITEM_UPDATED,
            WorkflowTrigger.DUE_DATE_ARRIVED,
          ],
        },
        {
          type: 'update-workitem',
          label: 'Change Assignee',
          icon: 'person',
          iconComponent: 'octicon',
          allowedWorkflowTriggers: [
            WorkflowTrigger.ITEM_CREATED,
            WorkflowTrigger.ITEM_UPDATED,
            WorkflowTrigger.DUE_DATE_ARRIVED,
          ],
        },
        {
          type: 'update-workitem',
          label: 'Change Point Effort',
          icon: 'barbell',
          iconComponent: 'custom-icon',
          allowedWorkflowTriggers: [
            WorkflowTrigger.ITEM_CREATED,
            WorkflowTrigger.ITEM_UPDATED,
            WorkflowTrigger.DUE_DATE_ARRIVED,
          ],
        },
        {
          type: 'update-workitem',
          label: 'Change Time Estimate',
          icon: 'clock',
          iconComponent: 'custom-icon',
          allowedWorkflowTriggers: [
            WorkflowTrigger.ITEM_CREATED,
            WorkflowTrigger.ITEM_UPDATED,
            WorkflowTrigger.DUE_DATE_ARRIVED,
          ],
        },
        {
          type: 'update-workitem',
          label: 'Change Cycle/Board',
          icon: 'column-outline',
          iconComponent: 'custom-icon',
          allowedWorkflowTriggers: [
            WorkflowTrigger.ITEM_CREATED,
            WorkflowTrigger.ITEM_UPDATED,
            WorkflowTrigger.DUE_DATE_ARRIVED,
          ],
        },
        {
          type: 'update-workitem',
          label: 'Change Custom Field',
          icon: 'custom-fields',
          iconComponent: 'custom-icon',
          allowedWorkflowTriggers: [
            WorkflowTrigger.ITEM_CREATED,
            WorkflowTrigger.ITEM_UPDATED,
            WorkflowTrigger.DUE_DATE_ARRIVED,
          ],
        },
        {
          type: 'update-workitem',
          label: 'Add Tags',
          icon: 'tag',
          iconComponent: 'octicon',
          allowedWorkflowTriggers: [
            WorkflowTrigger.ITEM_CREATED,
            WorkflowTrigger.ITEM_UPDATED,
            WorkflowTrigger.DUE_DATE_ARRIVED,
          ],
        },
        {
          type: 'update-workitem',
          label: 'Remove Tags',
          icon: 'tag',
          iconComponent: 'octicon',
          allowedWorkflowTriggers: [
            WorkflowTrigger.ITEM_CREATED,
            WorkflowTrigger.ITEM_UPDATED,
            WorkflowTrigger.DUE_DATE_ARRIVED,
          ],
        },
        {
          type: 'update-workitem',
          label: 'Set Due Date',
          icon: 'calendar',
          iconComponent: 'octicon',
          allowedWorkflowTriggers: [
            WorkflowTrigger.ITEM_CREATED,
            WorkflowTrigger.ITEM_UPDATED,
            WorkflowTrigger.DUE_DATE_ARRIVED,
          ],
        },
        {
          type: 'update-workitem',
          label: 'Change Time Spent',
          icon: 'stopwatch',
          iconComponent: 'octicon',
          allowedWorkflowTriggers: [
            WorkflowTrigger.ITEM_CREATED,
            WorkflowTrigger.ITEM_UPDATED,
            WorkflowTrigger.DUE_DATE_ARRIVED,
          ],
        },
      ],
    },
    {
      label: 'Workstream',
      icon: 'workstream-100',
      iconComponent: 'custom-icon',
      actions: [
        {
          type: 'create-folder',
          label: 'Create Workstream',
          icon: 'plus-circle',
          iconComponent: 'octicon',
        },
        {
          type: 'update-folder',
          label: 'Change Workstream Parent',
          icon: 'workstream-arrow',
          iconComponent: 'custom-icon',
          allowedWorkflowTriggers: [
            WorkflowTrigger.FOLDER_CREATED,
            WorkflowTrigger.FOLDER_UPDATED,
          ],
        },
        {
          type: 'update-folder',
          label: 'Change Workstream Color',
          icon: 'eyedropper',
          iconComponent: 'fluent-icon',
          allowedWorkflowTriggers: [
            WorkflowTrigger.FOLDER_CREATED,
            WorkflowTrigger.FOLDER_UPDATED,
          ],
        },
      ],
    },
    {
      label: 'Relationships',
      icon: 'organization',
      iconComponent: 'fluent-icon',
      actions: [
        {
          type: 'add-relationships',
          label: 'Create Relationships',
          icon: 'plus-circle',
          iconComponent: 'octicon',
        },
      ],
    },
  ];

  // #region Value variables (within actions)

  /**
   * Value variables differ from input variables in that these are supposed to be used as references to system compatible values
   * (tldr: don't use these for templating)
   **/

  type WorkflowInputValueVariableType =
    | 'itemNumber'
    | 'workType'
    | 'priority'
    | 'color'
    | 'team'
    | 'user'
    | 'board'
    | 'lane'
    | 'date'
    | 'tags';
  interface InputWorkItemValueVariable extends InputVariable {
    name: keyof WorkItem;
    valueType: WorkflowInputValueVariableType;
  }

  interface InputWorkstreamValueVariable extends InputVariable {
    name: keyof Folder;
    valueType: WorkflowInputValueVariableType;
  }

  const _workItemValueVariables: Readonly<Array<InputWorkItemValueVariable>> = [
    {
      label: 'Item',
      name: 'number',
      reference: `number`,
      valueType: 'itemNumber',
    },
    {
      label: 'Item Parent',
      name: 'parentNumber',
      reference: `parentNumber`,
      valueType: 'itemNumber',
    },
    {
      label: 'Item Type',
      name: 'workTypeId',
      reference: `workTypeId`,
      valueType: 'workType',
    },
    {
      label: 'Item Priority',
      name: 'priorityId',
      reference: `priorityId`,
      valueType: 'priority',
    },
    {
      label: 'Item Color',
      name: 'color',
      reference: `color`,
      valueType: 'color',
    },
    {
      label: 'Item Team',
      name: 'teamId',
      reference: `teamId`,
      valueType: 'team',
    },
    {
      label: 'Item Assignee',
      name: 'assigneeUsername',
      reference: `assigneeUsername`,
      valueType: 'user',
    },
    {
      label: 'Item Author',
      name: 'authorUsername',
      reference: `authorUsername`,
      valueType: 'user',
    },
    {
      label: 'Item Cycle/Board',
      name: 'boardId',
      reference: `boardId`,
      valueType: 'board',
    },
    {
      label: 'Item Status',
      name: 'laneId',
      reference: `laneId`,
      valueType: 'lane',
    },
    {
      label: 'Item Due Date',
      name: 'dueDate',
      reference: `dueDate`,
      valueType: 'date',
    },
    {
      label: 'Item Start Date',
      name: 'startDate',
      reference: `startDate`,
      valueType: 'date',
    },
    {
      label: 'Item Tags',
      name: 'labels',
      reference: `labels`,
      valueType: 'tags',
    },
  ];

  const _workstreamValueVariables: Readonly<
    Array<InputWorkstreamValueVariable>
  > = [
    {
      label: 'Workstream',
      name: 'number',
      reference: `number`,
      valueType: 'itemNumber',
    },
    {
      label: 'Workstream Parent',
      name: 'parentNumber',
      reference: `parentNumber`,
      valueType: 'itemNumber',
    },
    {
      label: 'Workstream Color',
      name: 'color',
      reference: `color`,
      valueType: 'color',
    },
    {
      label: 'Workstream Due Date',
      name: 'dueDate',
      reference: `dueDate`,
      valueType: 'date',
    },
    {
      label: 'Workstream Start Date',
      name: 'startDate',
      reference: `startDate`,
      valueType: 'date',
    },
    {
      label: 'Workstream Author',
      name: 'authorUsername',
      reference: `authorUsername`,
      valueType: 'user',
    },
  ];

  // #endregion

  const customFieldInputVariables = computed<
    (options?: {
      isUpdate?: boolean;
      isItemRequest?: boolean;
    }) => Array<InputVariable>
  >(() => {
    const { customFields } = useCustomFields({
      workTypeIds: ref([]),
    });
    const customFieldNameToVariableName = (name: string) => {
      const cleanedName = name.replace(/[^\w\s]/gi, '');
      return `item${camelCase(cleanedName)}`;
    };
    return ({
      isUpdate = false,
      isItemRequest = false,
    }: { isUpdate?: boolean; isItemRequest?: boolean } = {}) => {
      const inputPrefix: string = (() => {
        if (isItemRequest) return `$.input.workItem`;
        if (isUpdate) return `$.input.after`;
        return `$.input`;
      })();
      return (
        customFields.value?.map((field: CustomField) => {
          return {
            label: 'Item ' + field.name,
            name: customFieldNameToVariableName(field.name),
            reference: `${inputPrefix}.customFieldValues['${field.id}']`,
          };
        }) || []
      );
    };
  });

  const workItemContextInputVariables = computed<
    (options?: {
      isUpdate?: boolean;
      isItemRequest?: boolean;
      valueTypes?: Array<InputWorkItemValueVariable['valueType'] | 'primitive'>;
    }) => Array<InputVariable>
  >(() => {
    return ({
      isUpdate = false,
      isItemRequest = false,
      valueTypes,
    }: {
      /** whether the input variables are for an update trigger ([item|folder]-updated) [default: false] */
      isUpdate?: boolean;
      /** whether the trigger is for an item request; adjusts the reference path [default: false] */
      isItemRequest?: boolean;
      /** filter input variables by value type [default: everything is included] */
      valueTypes?: Array<InputWorkItemValueVariable['valueType'] | 'primitive'>;
    } = {}) => {
      const inputPrefix: string = (() => {
        if (isItemRequest) return `$.input.workItem`;
        if (isUpdate) return `$.input.after`;
        return `$.input`;
      })();
      const valueVars = _workItemValueVariables
        .filter(
          (variable) => !valueTypes || valueTypes.includes(variable.valueType)
        )
        .map((variable) => {
          return {
            label: variable.label,
            name: variable.name,
            reference: `${inputPrefix}.${variable.reference}`,
          };
        });

      const primitiveVars =
        !valueTypes || valueTypes.includes('primitive')
          ? [
              {
                label: 'Item Title',
                name: 'itemTitle',
                reference: `${inputPrefix}.title`,
              },
              {
                label: 'Item Parent Title',
                name: 'itemParentTitle',
                reference: `${inputPrefix}.parent.title`,
              },
              {
                label: 'Item Status',
                name: 'itemLane',
                reference: `${inputPrefix}.lane.name`,
              },
              {
                label: 'Item Priority',
                name: 'itemPriority',
                reference: `${inputPrefix}.priority.name`,
              },
              {
                label: 'Item Type',
                name: 'itemType',
                reference: `${inputPrefix}.workType.title`,
              },
              {
                label: 'Item Team',
                name: 'itemTeam',
                reference: `${inputPrefix}.team.name`,
              },
              {
                label: 'Item Assignee',
                name: 'itemAssignee',
                reference: `${inputPrefix}.assignee.name`,
              },
              {
                label: 'Item Number',
                name: 'itemNumber',
                reference: `${inputPrefix}.number`,
              },
              {
                label: 'Item Point Effort',
                name: 'itemPointEffort',
                reference: `${inputPrefix}.pointEffort`,
              },
              {
                label: 'Item Time Estimate',
                name: 'itemTimeEstimate',
                reference: `${inputPrefix}.estimate`,
              },
              // {
              //   label: 'Item Time Spent',
              //   name: 'itemTimeSpent',
              //   reference: `${inputPrefix}.timeSpentSeconds`,
              // },
              {
                label: 'Item Author Username',
                name: 'itemAuthorUsername',
                reference: `${inputPrefix}.author.name`,
              },
              {
                label: 'Item Blocker',
                name: 'itemBlocker',
                reference: `${inputPrefix}.blocker`,
              },
              // {
              //   label: 'Item Tags',
              //   name: 'itemTags',
              //   reference: `${inputPrefix}.labels`,
              // },
              {
                label: 'Item Cycle/Board',
                name: 'itemCycle',
                reference: `${inputPrefix}.board.label`,
              },
              {
                label: 'Item Due Date',
                name: 'itemDueDate',
                reference: `${inputPrefix}.dueDate`,
              },
              {
                label: 'Item Start Date',
                name: 'itemStartDate',
                reference: `${inputPrefix}.startDate`,
              },
              {
                label: 'Item Color',
                name: 'itemColor',
                reference: `${inputPrefix}.color`,
              },
              ...customFieldInputVariables.value({ isUpdate, isItemRequest }),
            ]
          : [];

      return [...valueVars, ...primitiveVars];
    };
  });

  const workstreamContextInputVariables = computed<
    (options?: {
      isUpdate?: boolean;
      valueTypes?: Array<InputWorkItemValueVariable['valueType'] | 'primitive'>;
    }) => Array<InputVariable>
  >(() => {
    return ({
      isUpdate = false,
      valueTypes,
    }: {
      /** whether the input variables are for an update trigger ([item|folder]-updated) [default: false] */
      isUpdate?: boolean;
      /** filter input variables by value type [default: everything is included] */
      valueTypes?: Array<
        InputWorkstreamValueVariable['valueType'] | 'primitive'
      >;
    } = {}) => {
      const inputPrefix: string = isUpdate ? `$.input.after` : `$.input`;

      const valueVars = _workstreamValueVariables
        .filter(
          (variable) => !valueTypes || valueTypes.includes(variable.valueType)
        )
        .map((variable) => {
          return {
            label: variable.label,
            name: variable.name,
            reference: `${inputPrefix}.${variable.reference}`,
          };
        });

      const primitiveVars =
        !valueTypes || valueTypes.includes('primitive')
          ? [
              {
                label: 'Workstream Title',
                name: 'workstreamTitle',
                reference: `${inputPrefix}.title`,
              },
              {
                label: 'Workstream Parent Title',
                name: 'workstreamParentTitle',
                reference: `${inputPrefix}.parent.title`,
              },
              {
                label: 'Workstream Color',
                name: 'workstreamColor',
                reference: `${inputPrefix}.color`,
              },
              {
                label: 'Workstream Number',
                name: 'workstreamNumber',
                reference: `${inputPrefix}.number`,
              },
              {
                label: 'Workstream Start Date',
                name: 'workstreamStartDate',
                reference: `${inputPrefix}.startDate`,
              },
              {
                label: 'Workstream Due Date',
                name: 'workstreamDueDate',
                reference: `${inputPrefix}.dueDate`,
              },
            ]
          : [];

      return [...valueVars, ...primitiveVars];
    };
  });

  const variablesToActionVariables = ({
    variables,
    actionIndex,
    actionUserReadableCount,
    variableType,
    isSelfAction,
  }: {
    variables: Array<InputVariable>;
    actionIndex: number;
    actionUserReadableCount: number;
    variableType: 'workitem' | 'workstream';
    isSelfAction?: boolean;
  }): Array<InputVariable> => {
    return variables.map((variable) => {
      const varTypeAccessorMap: Record<typeof variableType, string> = {
        workitem: '$.$items',
        workstream: '$.$streams',
      };
      return {
        ...variable,
        label: `Action #${actionUserReadableCount} ${
          isSelfAction ? '(Self)' : ''
        } ${variable.label}`,
        name: `action${actionUserReadableCount}_${variable.name}`,
        reference: variable.reference.startsWith('$.input')
          ? variable.reference.replace(
              '$.input',
              `${varTypeAccessorMap[variableType]}[${actionIndex}]`
            )
          : `${varTypeAccessorMap[variableType]}[${actionIndex}].${variable.reference}`,
      };
    });
  };

  const workItemContextActionVariables = computed<
    (opt: {
      /** $items doesn't account for non-work item related actions so its indices do not match the action indices */
      actionIndex: number;
      /** user readable count - accounting for all actions. 1 = first action, 2 = second action, etc. */
      actionUserReadableCount: number;
      valueTypes?: Array<InputWorkItemValueVariable['valueType'] | 'primitive'>;
      /** whether the variables are being used within the same action */
      isSelfAction: boolean;
      /** whether the current action is an update-[workitem|folder] action (assumed 'create' otherwise) */
      isUpdate: boolean;
    }) => Array<InputVariable>
  >(() => {
    const ret: (opt: {
      actionIndex: number;
      actionUserReadableCount: number;
      valueTypes?: Array<InputWorkItemValueVariable['valueType'] | 'primitive'>;
      isSelfAction: boolean;
      isUpdate: boolean;
    }) => Array<InputVariable> = ({
      actionIndex,
      actionUserReadableCount,
      valueTypes,
      isSelfAction,
      isUpdate,
    }) => {
      const valueVars: Array<InputVariable> = (() => {
        if (isSelfAction) return []; // don't allow referencing self values in any actions

        const toProcess: Array<InputWorkItemValueVariable> = [];

        if (!valueTypes) toProcess.push(..._workItemValueVariables);
        else {
          _workItemValueVariables.forEach((v) => {
            if (valueTypes.includes(v.valueType)) toProcess.push(v);
          });
        }

        return variablesToActionVariables({
          variables: toProcess,
          actionIndex,
          actionUserReadableCount,
          isSelfAction,
          variableType: 'workitem',
        });
      })();

      const primitiveVars: Array<InputVariable> = (() => {
        if (isSelfAction && !isUpdate) return []; // don't allow referencing self values in 'create' actions

        const toProcess: Array<InputVariable> = [];

        if (!valueTypes || valueTypes.includes('primitive'))
          toProcess.push(
            ...workItemContextInputVariables.value({
              valueTypes,
            })
          );

        return variablesToActionVariables({
          variables: toProcess,
          actionIndex,
          actionUserReadableCount,
          isSelfAction,
          variableType: 'workitem',
        });
      })();

      return [...valueVars, ...primitiveVars];
    };

    return ret;
  });

  const workstreamContextActionVariables = computed<
    (opt: {
      actionIndex: number;
      actionUserReadableCount: number;
      valueTypes?: Array<
        InputWorkstreamValueVariable['valueType'] | 'primitive'
      >;
      isSelfAction: boolean;
      isUpdate: boolean;
    }) => Array<InputVariable>
  >(() => {
    const ret = ({
      actionIndex,
      actionUserReadableCount,
      valueTypes,
      isSelfAction,
      isUpdate,
    }: {
      actionIndex: number;
      actionUserReadableCount: number;
      valueTypes?: Array<
        InputWorkstreamValueVariable['valueType'] | 'primitive'
      >;
      isSelfAction: boolean;
      isUpdate: boolean;
    }) => {
      const valueVars: Array<InputVariable> = (() => {
        if (isSelfAction) return []; // don't allow referencing self values in actions, only the primitives

        const toProcess: Array<InputWorkstreamValueVariable> = [];

        if (!valueTypes) toProcess.push(..._workstreamValueVariables);
        else {
          _workstreamValueVariables.forEach((v) => {
            if (valueTypes.includes(v.valueType)) toProcess.push(v);
          });
        }

        return variablesToActionVariables({
          variables: toProcess,
          actionIndex,
          actionUserReadableCount,
          isSelfAction,
          variableType: 'workstream',
        });
      })();

      const primitiveVars: Array<InputVariable> = (() => {
        if (isSelfAction && !isUpdate) return []; // don't allow referencing self values in 'create' actions

        const toProcess: Array<InputVariable> = [];

        if (!valueTypes || valueTypes.includes('primitive'))
          toProcess.push(
            ...workstreamContextInputVariables.value({
              valueTypes,
            })
          );

        return variablesToActionVariables({
          variables: toProcess,
          actionIndex,
          actionUserReadableCount,
          isSelfAction,
          variableType: 'workstream',
        });
      })();

      return [...valueVars, ...primitiveVars];
    };

    return ret;
  });

  function syncTriggerValuesToStoreBasedOnAttribute(
    attribute: string,
    currentBeforeCollection: Array<string | number>,
    currentAfterCollection: Array<string | number>,
    pathsNotEqualAttribute?: string, // in case it's different than attribute
    operator?: WorkflowOperator
  ) {
    let conditions: Array<WorkflowNestedCondition> = [
      {
        operator: WorkflowOperator.PATHS_NOT_EQUAL,
        value: [
          `$.before.${pathsNotEqualAttribute || attribute}`,
          `$.after.${pathsNotEqualAttribute || attribute}`,
        ],
        priority: 1000,
      },
      { any: [] }, // before conditions
      { any: [] }, // after conditions
    ];

    if (currentBeforeCollection.length === 1) {
      conditions[1] = {
        operator: operator || WorkflowOperator.EQUAL,
        value: currentBeforeCollection[0],
        path: `$.before.${attribute}`,
      };
    } else if (currentBeforeCollection.length > 1) {
      currentBeforeCollection.forEach((val) => {
        (conditions[1] as WorkflowAnyConditions)?.any?.push({
          operator: operator || WorkflowOperator.EQUAL,
          value: val,
          path: `$.before.${attribute}`,
        });
      });
    }

    if (currentAfterCollection.length === 1) {
      conditions[2] = {
        operator: operator || WorkflowOperator.EQUAL,
        value: currentAfterCollection[0],
        path: `$.after.${attribute}`,
      };
    } else if (currentAfterCollection.length > 1) {
      currentAfterCollection.forEach((val) => {
        (conditions[2] as WorkflowAnyConditions)?.any?.push({
          operator: operator || WorkflowOperator.EQUAL,
          value: val,
          path: `$.after.${attribute}`,
        });
      });
    }

    // filter out empty any conditions
    conditions = conditions.filter((condition) => {
      if ((condition as WorkflowAnyConditions).any) {
        return (condition as WorkflowAnyConditions).any.length > 0;
      }
      return true;
    });

    const workflowStore = useWorkflowStore();
    workflowStore.$patch((state) => {
      if (!state.dialogWorkflow?.triggerConditions) return;
      state.dialogWorkflow.triggerConditions = conditions;
    });
  }

  function syncCustomTriggerValuesToStore(
    attribute: string,
    customBeforeConditions?: WorkflowNestedCondition,
    customAfterConditions?: WorkflowNestedCondition,
    pathsNotEqualAttribute?: string // in case it's different than attribute
  ) {
    const conditions: Array<WorkflowNestedCondition> = [
      {
        operator: WorkflowOperator.PATHS_NOT_EQUAL,
        value: [
          `$.before.${pathsNotEqualAttribute || attribute}`,
          `$.after.${pathsNotEqualAttribute || attribute}`,
        ],
        priority: 1000,
      },
    ];

    if (customBeforeConditions) {
      conditions.push(customBeforeConditions);
    }
    if (customAfterConditions) {
      conditions.push(customAfterConditions);
    }

    const workflowStore = useWorkflowStore();
    workflowStore.$patch((state) => {
      if (!state.dialogWorkflow?.triggerConditions) return;
      state.dialogWorkflow.triggerConditions = conditions;
    });
  }

  function dayNth(d: number) {
    if (d > 3 && d < 21) return 'th';
    switch (d % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }

  function scheduledWorkflowSentenceSummary({
    frequency,
    interval,
    hour,
    weekdays,
    monthDays,
  }: TriggerTimeFrequencyInfo) {
    if (frequency === WorkflowTimeFrequency.DAILY) {
      if (interval === 1) {
        return `Every day at ${hourWithMeridiem(hour)}`;
      } else {
        return `Every ${interval} days at ${hourWithMeridiem(hour)}`;
      }
    } else if (frequency === WorkflowTimeFrequency.WEEKLY) {
      const sentenceStart = `Every ${interval > 1 ? interval + ' ' : ''}${
        interval === 1 ? 'week' : 'weeks'
      } on`;
      const weekdayFullNames = Object.keys(WEEKDAYS).map((w) =>
        startCase(w.toLowerCase())
      );
      const orderedSelectedWeekdayFullNames = Object.values(WEEKDAYS)
        .filter((weekday, index) => weekdays?.includes(index + 1))
        .map((weekday) => weekdayFullNames.find((w) => w.startsWith(weekday)));
      return `${sentenceStart} ${
        orderedSelectedWeekdayFullNames.join(', ') || 'Monday'
      } at ${hourWithMeridiem(hour)}`;
    } else if (frequency === WorkflowTimeFrequency.MONTHLY) {
      const sentenceStart = `Every ${interval > 1 ? interval + ' ' : ''}${
        interval === 1 ? 'month' : 'months'
      } on the`;

      const orderedSelectedMonthDays = [...(monthDays || [])]
        .sort((a, b) => a - b)
        .map((day) => `${day}${dayNth(day)}`);
      return `${sentenceStart} ${
        orderedSelectedMonthDays.join(', ') || '1st'
      } at ${hourWithMeridiem(hour)}`;
    }
    return '';
  }

  function extractTriggerTimeFrequencyInfo(
    workflow?: Workflow | CreateWorkflow
  ): TriggerTimeFrequencyInfo | undefined {
    if (!workflow || workflow.trigger !== WorkflowTrigger.SCHEDULE) {
      return undefined;
    }
    const conditions = workflow.triggerConditions as
      | Array<WorkflowConditionProperties>
      | undefined;
    if (!conditions) return undefined;

    let frequency: WorkflowTimeFrequency;
    let interval: number;
    let weekdays: Array<number> | undefined;
    let monthDays: Array<number> | undefined;

    // First check for day of year
    if (
      conditions.length === 2 &&
      conditions.find((c) => c.path === '$.hourOfDay') &&
      conditions.find((c) => c.path === '$.dayOfYear')
    ) {
      frequency = WorkflowTimeFrequency.DAILY;
      interval = conditions[1].value as number;
    } else if (
      conditions.length === 3 &&
      conditions.find((c) => c.path === '$.hourOfDay') &&
      conditions.find((c) => c.path === '$.dayOfWeek') &&
      conditions.find((c) => c.path === '$.weekOfYear')
    ) {
      frequency = WorkflowTimeFrequency.WEEKLY;
      interval = conditions[2].value as number;
      weekdays = conditions[1].value as Array<number>;
    } else if (
      conditions.length === 3 &&
      conditions.find((c) => c.path === '$.hourOfDay') &&
      conditions.find((c) => c.path === '$.dayOfMonth') &&
      conditions.find((c) => c.path === '$.monthOfYear')
    ) {
      frequency = WorkflowTimeFrequency.MONTHLY;
      interval = conditions[2].value as number;
      monthDays = conditions[1].value as Array<number>;
    } else {
      return undefined;
    }

    const hour = utcHourToLocalHour(conditions[0].value as number) || 7;

    return {
      frequency,
      interval,
      hour,
      weekdays,
      monthDays,
    };
  }

  function extractDueDateArrivedInfo(
    workflow?: Workflow | CreateWorkflow
  ): TriggerDueDateArrivedInfo | undefined {
    if (!workflow || workflow.trigger !== WorkflowTrigger.DUE_DATE_ARRIVED) {
      return undefined;
    }
    const inputParams = (workflow.inputParams ||
      {}) as DueDateArrivedInputParams;
    const { hourOfDay, daysBefore, daysAfter } = inputParams;
    if (
      !hourOfDay ||
      (!daysBefore && !daysAfter && daysAfter !== 0 && daysBefore !== 0)
    )
      return undefined;

    let frequency = inputParams.ui?.dueDateArrivedFrequency;
    if (!frequency) {
      frequency = 'days';
      const beforeOrAfter = daysBefore || daysAfter;
      if (beforeOrAfter) {
        if (beforeOrAfter >= 7) {
          frequency = 'weeks';
        }
        if (beforeOrAfter >= 30) {
          frequency = 'months';
        }
      }
    }

    let interval: number;
    let dateMethod: 'days' | 'weeks' | 'months';

    if (daysBefore && frequency === 'weeks') {
      interval = daysBefore / 7;
      dateMethod = 'weeks';
    } else if (daysBefore && frequency === 'months') {
      interval = daysBefore / 30;
      dateMethod = 'months';
    } else if (daysBefore) {
      interval = daysBefore;
      dateMethod = 'days';
    } else if (daysAfter && frequency === 'weeks') {
      interval = daysAfter / 7;
      dateMethod = 'weeks';
    } else if (daysAfter && frequency === 'months') {
      interval = daysAfter / 30;
      dateMethod = 'months';
    } else if (daysAfter) {
      interval = daysAfter;
      dateMethod = 'days';
    } else {
      interval = 0;
      dateMethod = 'days';
    }

    return {
      hourOfDay: utcHourToLocalHour(hourOfDay) || 7,
      interval,
      dateMethod,
      when: daysBefore ? 'before' : 'after',
    };
  }

  function getTriggerLabel(
    workflow: Workflow
  ): UIAutomationTrigger['label'] | undefined {
    if (
      ![WorkflowTrigger.ITEM_UPDATED, WorkflowTrigger.FOLDER_UPDATED].includes(
        workflow.trigger
      )
    ) {
      const triggerGroup = triggerGroups.find((tg) =>
        tg.triggers.find((t) => t.type === workflow.trigger)
      );
      if (triggerGroup) {
        const trigger = triggerGroup.triggers.find(
          (t) => t.type === workflow.trigger
        );
        if (trigger) return trigger.label;
      }
      return undefined;
    }
    // find the trigger label for item/folder updated
    const pathsNotEqualTriggerConditionFirstValue = (
      (
        workflow.triggerConditions?.find(
          (c) => (c as WorkflowConditionProperties).operator === 'pathsNotEqual'
        ) as WorkflowConditionProperties
      )?.value as Array<string | undefined>
    )[0];
    if (!pathsNotEqualTriggerConditionFirstValue) return undefined;
    if (pathsNotEqualTriggerConditionFirstValue.includes('parentNumber')) {
      return workflow.trigger === WorkflowTrigger.ITEM_UPDATED
        ? 'Item Parent Changed'
        : 'Workstream Parent Changed';
    } else if (pathsNotEqualTriggerConditionFirstValue.includes('color')) {
      return workflow.trigger === WorkflowTrigger.ITEM_UPDATED
        ? 'Item Color Changed'
        : 'Workstream Color Changed';
    } else if (pathsNotEqualTriggerConditionFirstValue.includes('laneId')) {
      return 'Status Changed';
    } else if (pathsNotEqualTriggerConditionFirstValue.includes('priorityId')) {
      return 'Priority Changed';
    } else if (pathsNotEqualTriggerConditionFirstValue.includes('workTypeId')) {
      return 'Item Type Changed';
    } else if (pathsNotEqualTriggerConditionFirstValue.includes('teamId')) {
      return 'Team Updated';
    } else if (
      pathsNotEqualTriggerConditionFirstValue.includes('assigneeUsername')
    ) {
      return 'Assignee Updated';
    } else if (
      pathsNotEqualTriggerConditionFirstValue.includes('pointEffort')
    ) {
      return 'Point Effort Updated';
    } else if (pathsNotEqualTriggerConditionFirstValue.includes('estimate')) {
      return 'Time Estimate Updated';
    } else if (
      pathsNotEqualTriggerConditionFirstValue.includes('boardId') ||
      pathsNotEqualTriggerConditionFirstValue.includes('boardType')
    ) {
      return 'Cycle/Board Updated';
    } else if (
      pathsNotEqualTriggerConditionFirstValue.includes('customFields')
    ) {
      return 'Custom Field Updated';
    } else if (pathsNotEqualTriggerConditionFirstValue.includes('labels')) {
      return 'Tags Updated';
    } else if (pathsNotEqualTriggerConditionFirstValue.includes('dueDate')) {
      return 'Due Date Changed';
    }
    return undefined;
  }

  function getActionLabel(
    action: WorkflowAction
  ): UIWorkflowAction['label'] | undefined {
    if (!action?.type) return;
    if (!['update-workitem', 'update-folder'].includes(action.type)) {
      const actionGroup = actionGroups.find((group) =>
        group.actions.some((a) => action?.type === a.type)
      ) as UIWorkflowActionGroup | undefined;
      if (!actionGroup) return;
      const matchingAction = actionGroup.actions.find(
        (a) => a.type === action?.type
      ) as UIWorkflowAction | undefined;
      if (!matchingAction) return;
      return matchingAction.label;
    }

    if (action.type === 'update-workitem') {
      const actionData = action.data as UpdateWorkItemActionData;
      if (!actionData) return;
      if (actionData.parentNumber) return 'Change Item Parent';
      if (actionData.color) return 'Change Item Color';
      if (actionData.laneId) return 'Change Status';
      if (actionData.assigneeUsername) return 'Change Assignee';
      if (actionData.teamId) return 'Change Team';
      if (actionData.boardId) return 'Change Cycle/Board';
      if (
        actionData.optimisticEstimate ||
        actionData.likelyEstimate ||
        actionData.pessimisticEstimate
      )
        return 'Change Time Estimate';
      if (actionData.pointEffort) return 'Change Point Effort';
      if (actionData.timeSpentSeconds) return 'Change Time Spent';
      if (actionData.addLabels) return 'Add Tags';
      if (actionData.removeLabels) return 'Remove Tags';
      if (actionData.priorityId) return 'Change Priority';
      if (actionData.workTypeId) return 'Change Item Type';
      if (actionData.customFields) return 'Change Custom Field';
      if (actionData.dueDate) return 'Set Due Date';
    } else if (action.type === 'update-folder') {
      const actionData = action.data as UpdateFolderActionData;
      if (actionData.parentNumber) return 'Change Workstream Parent';
      if (actionData.color) return 'Change Workstream Color';
    }
    return undefined;
  }

  function filterWorkflows(
    workflows: Array<Workflow>,
    filters: WorkflowFilterOptions
  ): Array<Workflow> {
    let workflowsCopy = JSON.parse(
      JSON.stringify(workflows)
    ) as Array<Workflow>;

    if (filters.q) {
      workflowsCopy = workflowsCopy.filter(
        (w) =>
          !!w.description &&
          w.description.toLowerCase().includes(filters.q?.toLowerCase() || '')
      );
    }

    if (filters.createdByIds?.length) {
      workflowsCopy = workflowsCopy.filter((w) =>
        filters.createdByIds?.includes(w.createdById)
      );
    }

    if (filters.status?.length) {
      workflowsCopy = workflowsCopy.filter((w) => {
        if (w.disabled) return filters.status?.includes('disabled');
        if (!w.disabled) return filters.status?.includes('active');
      });
    }

    if (filters.triggers?.length) {
      workflowsCopy = workflowsCopy.filter((w) => {
        const label = getTriggerLabel(w);
        if (!label) return false;
        return !!filters.triggers?.find((t) => {
          return t.label === label && t.type === w.trigger;
        });
      });
    }

    if (filters.conditionFields?.length) {
      workflowsCopy = workflowsCopy.filter((w) => {
        const workflowTranslatedConditions = translatedNestedConditions(w);
        if (!workflowTranslatedConditions.length) return false;
        const conditionsFields = workflowTranslatedConditions.map(
          (c) => c.field
        );
        return !!filters.conditionFields?.find((f) =>
          conditionsFields.includes(f.field)
        );
      });
    }

    if (filters.actionTypes?.length) {
      workflowsCopy = workflowsCopy.filter((w) => {
        const workflowActions = w.actions;
        if (!workflowActions.length) return false;
        return !!filters.actionTypes?.find((a) => {
          return !!workflowActions.find((wa) => {
            return wa.type === a.type && getActionLabel(wa) === a.label;
          });
        });
      });
    }

    return workflowsCopy;
  }

  return {
    triggerGroups,
    actionGroups,
    workItemConditions,
    workstreamConditions,
    workItemContextInputVariables,
    workstreamContextInputVariables,
    workItemContextActionVariables,
    workstreamContextActionVariables,
    variablesToActionVariables,
    syncTriggerValuesToStoreBasedOnAttribute,
    syncCustomTriggerValuesToStore,
    scheduledWorkflowSentenceSummary,
    extractTriggerTimeFrequencyInfo,
    extractDueDateArrivedInfo,
    getTriggerLabel,
    filterWorkflows,
    getActionLabel,
  };
}

export function translatedNestedConditions(
  workflow?: Workflow | CreateWorkflow
): Array<WorkflowUIConditionProperties> {
  if (!workflow?.nestedConditions?.length) {
    return [];
  }
  const translatedConditions: Array<WorkflowUIConditionProperties> = [];
  for (const condition of workflow.nestedConditions) {
    if ((condition as WorkflowConditionProperties).operator) {
      translatedConditions.push(
        translateWorkflowSingleCondition(
          condition as WorkflowConditionProperties
        )
      );
    } else if ((condition as WorkflowAnyConditions).any) {
      translatedConditions.push(
        translateWorkflowAnyCondition(condition as WorkflowAnyConditions)
      );
    } else if ((condition as WorkflowAllConditions).all) {
      translatedConditions.push(
        translateWorkflowAllCondition(condition as WorkflowAllConditions)
      );
    }
  }
  return translatedConditions;
}

type WorkflowPrimitiveValue = string | number | boolean;

function toWorkflowPrimitiveValue(value: unknown): WorkflowPrimitiveValue {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }
  // Fallback for unexpected types - convert to string
  return String(value ?? '');
}

function translateWorkflowSingleCondition(
  condition: WorkflowConditionProperties
): WorkflowUIConditionProperties {
  return {
    operator: condition.operator,
    field: (condition.path as string | undefined)?.split('$.')[1] || '',
    value: [toWorkflowPrimitiveValue(condition.value)],
  };
}

function translateWorkflowAnyCondition(
  condition: WorkflowAnyConditions
): WorkflowUIConditionProperties {
  const firstCondition = condition.any[0] as WorkflowConditionProperties;
  return {
    operator: firstCondition?.operator,
    field: (firstCondition?.path as string | undefined)?.split('$.')[1] || '',
    value: condition.any.map((c) =>
      toWorkflowPrimitiveValue((c as WorkflowConditionProperties).value)
    ),
  };
}

function translateWorkflowAllCondition(
  condition: WorkflowAllConditions
): WorkflowUIConditionProperties {
  const firstCondition = condition.all[0] as WorkflowConditionProperties;
  return {
    operator: firstCondition?.operator,
    field: (firstCondition?.path as string | undefined)?.split('$.')[1] || '',
    value: condition.all.map((c) =>
      toWorkflowPrimitiveValue((c as WorkflowConditionProperties).value)
    ),
  };
}

export function getOptionalOperatorFromTriggerConditions(
  triggerConditions?: Array<WorkflowNestedCondition>
): WorkflowOperator | undefined {
  return (
    triggerConditions?.find(
      (condition) =>
        (condition as WorkflowConditionProperties).value === null &&
        !!(condition as WorkflowConditionProperties).operator
    ) as WorkflowConditionProperties | undefined
  )?.operator;
}
